/* eslint-disable max-lines */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { cn } from "tailwind-variants";

import { CSS_COLORS, POPULAR_COLORS } from "./cssColors";

type ColorComboBoxProps = {
  value: string;
  onChange: (value: string) => void;
  onColorChange: (hex: string) => void;
  className?: string;
  placeholder?: string;
  size?: "sm" | "md" | "lg";
  dropdownPlacement?: "bottom" | "left" | "top";
  selectedColorHex?: string;
  dropdownWidth?: string;
};

/* eslint-disable max-lines-per-function */
export function ColorComboBox({
  value,
  onChange,
  onColorChange,
  className,
  placeholder = "white",
  size = "md",
  dropdownPlacement = "bottom",
  selectedColorHex,
  dropdownWidth = "w-full"
}: ColorComboBoxProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [filteredColors, setFilteredColors] = useState(POPULAR_COLORS);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [dropdownPosition, setDropdownPosition] = useState<{
    top?: number;
    bottom?: number;
    left: number;
    width: number;
    maxHeight?: number;
  }>({ left: 0, width: 0 });
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const hasPositionedRef = useRef(false);

  const sizeClasses = {
    sm: "h-8 px-2 text-base",
    md: "h-9 px-3 text-base",
    lg: "h-10 px-3 text-base"
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        inputRef.current &&
        !inputRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const updateDropdownPosition = useCallback(() => {
    // Only calculate position once per open session to prevent jumps on mobile keyboard
    if (hasPositionedRef.current) {
      return;
    }

    if (inputRef.current) {
      const rect = inputRef.current.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const defaultMaxHeight = 240; // max-h-60 = 15rem = 240px
      const margin = 8; // Safety margin from viewport edge

      if (dropdownPlacement === "left") {
        // Position so bottom of dropdown aligns with bottom of input
        setDropdownPosition({
          bottom: viewportHeight - rect.bottom, // Distance from viewport bottom to input bottom
          left: rect.left - 256 - 8, // 256px is w-64, 8px is mr-2
          width: 256
        });
      } else if (dropdownPlacement === "top") {
        // Position above the input
        setDropdownPosition({
          bottom: viewportHeight - rect.top + 4, // Distance from viewport bottom to input top + margin
          left: rect.left,
          width: rect.width
        });
      } else {
        // Default: position below the input with height constrained to available space
        const spaceBelow = viewportHeight - rect.bottom - 4 - margin;
        setDropdownPosition({
          top: rect.bottom + 4, // mt-1
          left: rect.left,
          width: rect.width,
          maxHeight: Math.min(spaceBelow, defaultMaxHeight)
        });
      }

      hasPositionedRef.current = true;
    }
  }, [dropdownPlacement]);

  const handleInputChange = useCallback(
    (inputValue: string) => {
      onChange(inputValue);
      setHighlightedIndex(-1);

      // Check if it's a valid hex code
      if (/^#[0-9A-Fa-f]{6}$/.test(inputValue)) {
        onColorChange(inputValue);
      } else {
        // Check if it's a CSS color name
        const matchingColor = CSS_COLORS.find(
          c => c.name.toLowerCase() === inputValue.toLowerCase()
        );
        if (matchingColor) {
          onColorChange(matchingColor.hex);
        }
      }

      // Filter colors based on input with relevance sorting
      if (inputValue.trim()) {
        const searchTerm = inputValue.toLowerCase();
        const matches = CSS_COLORS.filter(
          color =>
            color.name.toLowerCase().includes(searchTerm) ||
            color.hex.toLowerCase().includes(searchTerm)
        );

        // Sort by relevance: exact match > starts with > contains
        const sorted = matches.sort((a, b) => {
          const aName = a.name.toLowerCase();
          const bName = b.name.toLowerCase();
          const aHex = a.hex.toLowerCase();
          const bHex = b.hex.toLowerCase();

          // Exact match has highest priority
          const aExact = aName === searchTerm || aHex === searchTerm ? 0 : 1;
          const bExact = bName === searchTerm || bHex === searchTerm ? 0 : 1;
          if (aExact !== bExact) return aExact - bExact;

          // Starts with has second priority
          const aStarts = aName.startsWith(searchTerm) || aHex.startsWith(searchTerm) ? 0 : 1;
          const bStarts = bName.startsWith(searchTerm) || bHex.startsWith(searchTerm) ? 0 : 1;
          if (aStarts !== bStarts) return aStarts - bStarts;

          // Then alphabetical order
          return aName.localeCompare(bName);
        });

        setFilteredColors(sorted.slice(0, 15));
      } else {
        setFilteredColors(POPULAR_COLORS);
      }

      updateDropdownPosition();
      setIsOpen(true);
    },
    [onChange, onColorChange, updateDropdownPosition]
  );

  const handleSelectColor = useCallback(
    (colorName: string, colorHex: string) => {
      onChange(colorName);
      onColorChange(colorHex);
      setIsOpen(false);
      setHighlightedIndex(-1);
      inputRef.current?.blur();
    },
    [onChange, onColorChange]
  );

  const handleInputFocus = () => {
    setFilteredColors(POPULAR_COLORS);
    setHighlightedIndex(-1);
    updateDropdownPosition();
    setIsOpen(true);
  };

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (!isOpen) {
        if (e.key === "ArrowDown" || e.key === "ArrowUp") {
          e.preventDefault();
          setIsOpen(true);
          setHighlightedIndex(0);
        }
        return;
      }

      if (e.key === "ArrowDown") {
        e.preventDefault();
        setHighlightedIndex(prev => (prev < filteredColors.length - 1 ? prev + 1 : prev));
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setHighlightedIndex(prev => (prev > 0 ? prev - 1 : 0));
      } else if (e.key === "Enter") {
        e.preventDefault();
        if (highlightedIndex >= 0 && highlightedIndex < filteredColors.length) {
          const selectedColor = filteredColors[highlightedIndex];
          if (selectedColor) {
            handleSelectColor(selectedColor.name, selectedColor.hex);
          }
        }
      } else if (e.key === "Escape") {
        e.preventDefault();
        setIsOpen(false);
        setHighlightedIndex(-1);
      } else if (e.key === "Tab") {
        setIsOpen(false);
        setHighlightedIndex(-1);
      }
    },
    [isOpen, filteredColors, highlightedIndex, handleSelectColor]
  );

  // Scroll highlighted item into view
  useEffect(() => {
    if (highlightedIndex >= 0 && dropdownRef.current) {
      const highlightedElement = dropdownRef.current.children[highlightedIndex] as HTMLElement;
      if (highlightedElement) {
        highlightedElement.scrollIntoView({ block: "nearest", behavior: "smooth" });
      }
    }
  }, [highlightedIndex]);

  // Reset position tracking when dropdown closes
  useEffect(() => {
    if (!isOpen) {
      hasPositionedRef.current = false;
    }
  }, [isOpen]);

  const dropdownClasses = cn(
    "fixed z-[100] max-h-60 overflow-auto rounded-lg border border-gray-200 bg-white shadow-lg",
    dropdownWidth
  );

  const displayColorHex = selectedColorHex || "#ffffff";
  const inputPaddingRight = "pr-10";

  const dropdownStyle = useMemo<React.CSSProperties>(() => {
    const style: React.CSSProperties = {
      left: `${dropdownPosition.left}px`,
      width: `${dropdownPosition.width}px`
    };

    if (dropdownPosition.bottom !== undefined) {
      style.bottom = `${dropdownPosition.bottom}px`;
    } else if (dropdownPosition.top !== undefined) {
      style.top = `${dropdownPosition.top}px`;
    }

    if (dropdownPosition.maxHeight !== undefined) {
      style.maxHeight = `${dropdownPosition.maxHeight}px`;
    }

    return style;
  }, [dropdownPosition]);

  return (
    <div className={cn("relative overflow-visible", className)}>
      <div className="relative overflow-visible">
        <input
          ref={inputRef}
          type="text"
          value={value}
          onChange={e => handleInputChange(e.target.value)}
          onFocus={handleInputFocus}
          onKeyDown={handleKeyDown}
          className={cn(
            "w-full rounded-lg border border-gray-300 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500",
            sizeClasses[size],
            inputPaddingRight
          )}
          placeholder={placeholder}
          data-testid="color-combobox-input"
        />
        <div
          className="pointer-events-none absolute right-2 top-1/2 size-6 -translate-y-1/2 rounded-full border border-gray-300"
          style={{ backgroundColor: displayColorHex }}
        />
      </div>

      {isOpen &&
        filteredColors.length > 0 &&
        typeof document !== "undefined" &&
        createPortal(
          <div
            ref={dropdownRef}
            className={dropdownClasses}
            style={dropdownStyle}
            data-testid="color-combobox-dropdown"
          >
            {filteredColors.map((color, index) => {
              const isSelected =
                value.toLowerCase() === color.name.toLowerCase() ||
                (selectedColorHex && selectedColorHex.toLowerCase() === color.hex.toLowerCase());
              const isHighlighted = index === highlightedIndex;
              return (
                <button
                  key={color.name}
                  type="button"
                  onClick={() => handleSelectColor(color.name, color.hex)}
                  className={cn(
                    "flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-gray-100",
                    isSelected && "bg-blue-50",
                    isHighlighted && "bg-gray-100"
                  )}
                  data-testid={`color-option-${color.name}`}
                >
                  <div
                    className="size-4 shrink-0 rounded-full border border-gray-300"
                    style={{ backgroundColor: color.hex }}
                  />
                  <span className="capitalize">{color.name}</span>
                  <span className="ml-auto font-mono text-xs text-gray-500">{color.hex}</span>
                </button>
              );
            })}
          </div>,
          document.body
        )}
    </div>
  );
}
